// The Strategy Builder's engine: a small, graphical-pine-script-like rule
// model (IF <series> <comparator> <series|value> THEN enter/exit), plus a
// bar-by-bar backtester. Strategies are plain JSON in chrome.storage so
// they sync across chart windows.

import type { OHLCPoint } from "./market-data.js";
import { adxSeries, atrSeries, bollingerBandSeries, emaSeries, macdSeries, rsiSeries, simpleMovingAverage, stochasticSeries, vwapSeries } from "./chart-indicators.js";
import { patternKinds, patternLabels, patternSeries, type PatternKind } from "./candle-patterns.js";
import { Emitter, onStorageChange, storageGet, storageSet } from "./storage.js";

export type OperandKind =
  | "close"
  | "open"
  | "high"
  | "low"
  | "sma20"
  | "sma50"
  | "sma200"
  | "ema20"
  | "ema50"
  | "vwap"
  | "bbUpper"
  | "bbLower"
  | "rsi14"
  | "atr14"
  | "macdHist"
  | "volume"
  | "priorHigh20"
  | "priorLow20"
  | "volSma20"
  | "priorDayHigh"
  | "priorDayLow"
  | "priorWeekHigh"
  | "priorWeekLow"
  | "nearestSupport"
  | "nearestResistance"
  | "swingHigh"
  | "swingLow"
  | "pivotP"
  | "pivotR1"
  | "pivotR2"
  | "pivotS1"
  | "pivotS2"
  | "stochK"
  | "stochD"
  | "adx14"
  | "higherHigh"
  | "lowerLow"
  | "gapUp"
  | "gapDown"
  | PatternKind
  | "value";

export const operandLabels: Record<OperandKind, string> = {
  close: "Close",
  open: "Open",
  high: "High",
  low: "Low",
  sma20: "SMA 20",
  sma50: "SMA 50",
  sma200: "SMA 200",
  ema20: "EMA 20",
  ema50: "EMA 50",
  vwap: "VWAP",
  bbUpper: "BB Upper",
  bbLower: "BB Lower",
  rsi14: "RSI 14",
  atr14: "ATR 14",
  macdHist: "MACD hist",
  volume: "Volume",
  priorHigh20: "Prior high (20)",
  priorLow20: "Prior low (20)",
  volSma20: "Avg volume (20)",
  priorDayHigh: "Prior day high",
  priorDayLow: "Prior day low",
  priorWeekHigh: "Prior week high",
  priorWeekLow: "Prior week low",
  nearestSupport: "Nearest support",
  nearestResistance: "Nearest resistance",
  swingHigh: "Swing high",
  swingLow: "Swing low",
  pivotP: "Pivot P",
  pivotR1: "Pivot R1",
  pivotR2: "Pivot R2",
  pivotS1: "Pivot S1",
  pivotS2: "Pivot S2",
  stochK: "Stochastic %K",
  stochD: "Stochastic %D",
  adx14: "ADX 14",
  higherHigh: "Higher high",
  lowerLow: "Lower low",
  gapUp: "Gap up",
  gapDown: "Gap down",
  ...patternLabels,
  value: "Number…",
};

/** Operand groups for the rule editor's dropdown — the list got long, so
 * it's organized the way traders think, not one flat menu. */
export const operandGroups: Array<{ label: string; kinds: OperandKind[] }> = [
  { label: "Price", kinds: ["close", "open", "high", "low"] },
  { label: "Indicators", kinds: ["sma20", "sma50", "sma200", "ema20", "ema50", "vwap", "bbUpper", "bbLower", "rsi14", "atr14", "macdHist", "stochK", "stochD", "adx14", "volume", "volSma20"] },
  { label: "Price action", kinds: [...patternKinds, "higherHigh", "lowerLow", "gapUp", "gapDown"] },
  { label: "Levels", kinds: ["priorHigh20", "priorLow20", "priorDayHigh", "priorDayLow", "priorWeekHigh", "priorWeekLow", "nearestSupport", "nearestResistance", "swingHigh", "swingLow", "pivotP", "pivotR1", "pivotR2", "pivotS1", "pivotS2"] },
  { label: "Value", kinds: ["value"] },
];

/** Boolean "event" operands read "<name> appears on this bar" and need no
 * comparator (stored as value > 0.5). */
const eventOperands = new Set<OperandKind>([...patternKinds, "higherHigh", "lowerLow", "gapUp", "gapDown"]);
export function isEventOperand(kind: OperandKind): boolean {
  return eventOperands.has(kind);
}

/** Pattern operands are boolean events, not price series — the UI shows
 * them as "<pattern> appears on this bar" (stored as value > 0.5). */
export function isPatternOperand(kind: OperandKind): kind is PatternKind {
  return (patternKinds as string[]).includes(kind);
}

export type Comparator = "crossesAbove" | "crossesBelow" | "greaterThan" | "lessThan";

export const comparatorLabels: Record<Comparator, string> = {
  crossesAbove: "crosses above",
  crossesBelow: "crosses below",
  greaterThan: "is above",
  lessThan: "is below",
};

export interface Operand {
  kind: OperandKind;
  value?: number;
}

export interface Rule {
  left: Operand;
  cmp: Comparator;
  right: Operand;
  /** Confluence weight (entryMode "score"). Default 1. */
  weight?: number;
}

/** A higher-timeframe filter layer: ALL rules must hold on the LAST
 * CLOSED bar of that timeframe. This is how traders actually think —
 * "4H trend up, 15m setup forming, enter on the 5m trigger" — and the
 * closed-bar rule is what keeps backtests honest (a still-forming
 * higher-timeframe bar would leak the future / repaint). */
export interface StrategyLayer {
  /** One of layerTimeframes below. */
  timeframe: string;
  rules: Rule[];
}

/** Timeframes a filter layer may use, with their bucket size. */
export const layerTimeframes: Array<{ id: string; label: string; minutes: number }> = [
  { id: "15m", label: "15 Min", minutes: 15 },
  { id: "30m", label: "30 Min", minutes: 30 },
  { id: "1h", label: "1 Hour", minutes: 60 },
  { id: "4h", label: "4 Hour", minutes: 240 },
  { id: "1d", label: "1 Day", minutes: 1440 },
  { id: "1wk", label: "1 Week", minutes: 10080 },
];

export interface Strategy {
  id: string;
  name: string;
  direction: "long" | "short";
  /** Optional highest-timeframe regime filter ("is the trend even up?"). */
  trend?: StrategyLayer;
  /** Optional intermediate-timeframe setup filter. */
  setup?: StrategyLayer;
  /** All entry rules must hold (AND) — on the execution timeframe.
   * In entryMode "score", each holding rule contributes its weight and
   * entry fires when the sum reaches minScore instead. */
  entry: Rule[];
  /** "all" (default): every entry rule must hold. "score": weighted
   * confluence — most confirmations, not necessarily every one. */
  entryMode?: "all" | "score";
  /** Score threshold for entryMode "score". */
  minScore?: number;
  /** Any exit rule closes the position (OR), in addition to TP/SL. */
  exit: Rule[];
  takeProfitPct?: number;
  stopLossPct?: number;
  /** Optional round-trip transaction cost, % of price per trade
   * (entry+exit). 0/undefined = frictionless (and the UI says so). */
  costPct?: number;
  /** What "good" means for this strategy — different traders optimize
   * different metrics (a combine is a survival test; own-money growth
   * is a Sharpe test; sheltering capital is a drawdown test). Drives
   * which metrics the result panel emphasizes and how the verdict is
   * phrased. Default: "grow". */
  goal?: StrategyGoal;
  /** Prop-firm rules (goal "combine"): dollar limits checked against the
   * backtest equity curve scaled to the account size. */
  propRules?: PropRules;
}

export type StrategyGoal = "combine" | "grow" | "shelter";

export const goalLabels: Record<StrategyGoal, string> = {
  combine: "Pass a combine",
  grow: "Grow my account",
  shelter: "Protect capital",
};

/** One line on which metrics matter for each goal — most users won't
 * know, and the difference is the whole point. */
export const goalHints: Record<StrategyGoal, string> = {
  combine: "A combine is a survival test: Max drawdown and your worst day decide it — returns only count if the account lives.",
  grow: "Growing your own money: risk-adjusted return is king — watch Sharpe and total return against just holding.",
  shelter: "Protecting capital: shallow drawdowns and calm downside (Sortino) matter more than squeezing out return.",
};

export interface PropRules {
  /** Simulated account size the equity curve is scaled to, $. */
  accountSize: number;
  /** Max loss allowed in one ET day, $. */
  dailyLossLimit: number;
  /** Trailing max drawdown from the equity high-water mark, $. */
  maxDrawdown: number;
}

export interface PropCheck {
  passed: boolean;
  breachType: "daily" | "trailing" | null;
  /** Epoch ms of the bar where the first breach happened. */
  breachTime: number | null;
  worstDailyLossUsd: number;
  maxTrailingDrawdownUsd: number;
}

const etDayKey = new Intl.DateTimeFormat("en-CA", { timeZone: "America/New_York" });

/**
 * Replays the strategy's per-bar returns on a prop account and checks the
 * two rules every firm enforces: the daily loss limit and the trailing
 * max drawdown. Reports the FIRST breach (a real eval ends there) plus
 * the worst-day / deepest-trailing numbers across the whole window.
 */
export function propCheck(barReturns: number[], candles: OHLCPoint[], rules: PropRules): PropCheck {
  let equity = rules.accountSize;
  let peak = equity;
  let dayStart = equity;
  let currentDay = "";
  let worstDaily = 0;
  let worstTrailing = 0;
  let breachType: "daily" | "trailing" | null = null;
  let breachTime: number | null = null;

  for (let i = 0; i < barReturns.length && i < candles.length; i++) {
    const day = etDayKey.format(candles[i]!.date);
    if (day !== currentDay) {
      currentDay = day;
      dayStart = equity;
    }
    equity *= 1 + barReturns[i]!;
    peak = Math.max(peak, equity);
    const dailyLoss = dayStart - equity;
    const trailing = peak - equity;
    worstDaily = Math.max(worstDaily, dailyLoss);
    worstTrailing = Math.max(worstTrailing, trailing);
    if (breachType == null) {
      if (dailyLoss >= rules.dailyLossLimit) {
        breachType = "daily";
        breachTime = candles[i]!.date.getTime();
      } else if (trailing >= rules.maxDrawdown) {
        breachType = "trailing";
        breachTime = candles[i]!.date.getTime();
      }
    }
  }
  return {
    passed: breachType == null,
    breachType,
    breachTime,
    worstDailyLossUsd: worstDaily,
    maxTrailingDrawdownUsd: worstTrailing,
  };
}

export function newStrategy(): Strategy {
  return {
    id: crypto.randomUUID(),
    name: "New strategy",
    direction: "long",
    entry: [{ left: { kind: "close" }, cmp: "crossesAbove", right: { kind: "sma20" } }],
    exit: [{ left: { kind: "close" }, cmp: "crossesBelow", right: { kind: "sma20" } }],
    takeProfitPct: undefined,
    stopLossPct: undefined,
  };
}

/** Aligns every operand series to the candle array (null = not yet defined). */
export function seriesFor(kind: OperandKind, candles: OHLCPoint[], isIntraday: boolean, value?: number): (number | null)[] {
  const byTime = (points: Array<{ time: number; value: number }>) => {
    const map = new Map(points.map((p) => [p.time, p.value]));
    return candles.map((c) => map.get(c.date.getTime()) ?? null);
  };
  switch (kind) {
    case "close":
      return candles.map((c) => c.close);
    case "open":
      return candles.map((c) => c.open);
    case "high":
      return candles.map((c) => c.high);
    case "low":
      return candles.map((c) => c.low);
    case "sma20":
      return byTime(simpleMovingAverage(candles, 20));
    case "sma50":
      return byTime(simpleMovingAverage(candles, 50));
    case "sma200":
      return byTime(simpleMovingAverage(candles, 200));
    case "vwap":
      return byTime(vwapSeries(candles, isIntraday));
    case "bbUpper":
      return byTime(bollingerBandSeries(candles).map((p) => ({ time: p.time, value: p.upper })));
    case "bbLower":
      return byTime(bollingerBandSeries(candles).map((p) => ({ time: p.time, value: p.lower })));
    case "ema20":
      return byTime(emaSeries(candles, 20));
    case "ema50":
      return byTime(emaSeries(candles, 50));
    case "rsi14":
      return byTime(rsiSeries(candles, 14));
    case "atr14":
      return byTime(atrSeries(candles, 14));
    case "macdHist":
      return byTime(macdSeries(candles).map((p) => ({ time: p.time, value: p.histogram })));
    case "volume":
      return candles.map((c) => c.volume);
    // Rolling extremes over the PREVIOUS 20 bars, excluding the current
    // one — "the obvious level" for sweep/reclaim (liquidity-grab) rules.
    // Including the current bar would make "Low below prior low" almost
    // unsatisfiable and self-referential.
    case "priorHigh20":
      return rollingExtreme(candles, 20, "high");
    case "priorLow20":
      return rollingExtreme(candles, 20, "low");
    case "priorDayHigh":
      return priorBucketExtreme(candles, 1440, "high");
    case "priorDayLow":
      return priorBucketExtreme(candles, 1440, "low");
    case "priorWeekHigh":
      return priorBucketExtreme(candles, 10080, "high");
    case "priorWeekLow":
      return priorBucketExtreme(candles, 10080, "low");
    case "nearestSupport":
      return nearestSwingLevel(candles, "support");
    case "nearestResistance":
      return nearestSwingLevel(candles, "resistance");
    case "swingHigh":
      return latestSwingSeries(candles, "high");
    case "swingLow":
      return latestSwingSeries(candles, "low");
    case "higherHigh":
      return structureEventSeries(candles, "high");
    case "lowerLow":
      return structureEventSeries(candles, "low");
    case "gapUp":
      return candles.map((c, i) => (i === 0 ? null : c.open > candles[i - 1]!.close ? 1 : 0));
    case "gapDown":
      return candles.map((c, i) => (i === 0 ? null : c.open < candles[i - 1]!.close ? 1 : 0));
    case "pivotP":
    case "pivotR1":
    case "pivotR2":
    case "pivotS1":
    case "pivotS2":
      return pivotLevelSeries(candles, kind);
    case "stochK":
      return byTime(stochasticSeries(candles).map((p) => ({ time: p.time, value: p.k })));
    case "stochD":
      return byTime(stochasticSeries(candles).map((p) => ({ time: p.time, value: p.d })));
    case "adx14":
      return byTime(adxSeries(candles));
    case "patBullEngulf":
    case "patBearEngulf":
    case "patHammer":
    case "patStar":
    case "patDoji":
    case "patInside":
    case "patOutside":
      return patternSeries(kind, candles);
    case "volSma20":
      return candles.map((c, i) => {
        if (i < 19) return null;
        let sum = 0;
        for (let j = i - 19; j <= i; j++) sum += candles[j]!.volume;
        return sum / 20;
      });
    case "value":
      return candles.map(() => value ?? 0);
  }
}

/** The high/low of the last fully CLOSED day/week bucket at each bar —
 * "yesterday's high" on an intraday chart. Buckets are epoch-aligned
 * (UTC), an honest approximation of the session day; the current,
 * still-forming bucket is never used (same no-peek rule as layers). */
function priorBucketExtreme(candles: OHLCPoint[], minutes: number, side: "high" | "low"): (number | null)[] {
  const buckets = resampleCandles(candles, minutes);
  const sizeMs = minutes * 60_000;
  return candles.map((c) => {
    const t = c.date.getTime();
    let value: number | null = null;
    for (const b of buckets) {
      if (b.date.getTime() + sizeMs <= t) value = side === "high" ? b.high : b.low;
      else break;
    }
    return value;
  });
}

/**
 * Structural support/resistance from CONFIRMED swing points: a swing low
 * is a bar whose low is the lowest of its k=3 neighbors either side, and
 * it only becomes usable k bars AFTER it forms (before that you couldn't
 * have known — using it earlier would repaint). Per bar, the nearest
 * confirmed swing low below the close (support) or swing high above it
 * (resistance), from the most recent 50 swings.
 */
const SWING_K = 3;

interface Swing {
  confirmedAt: number; // bar index from which this swing is usable (formed + k)
  value: number;
}

/** Confirmed swing highs or lows via k=3 fractals. A swing is only usable
 * k bars AFTER it forms — before that you couldn't have known it was a
 * pivot, so using it earlier would repaint. */
function confirmedSwings(candles: OHLCPoint[], side: "high" | "low"): Swing[] {
  const swings: Swing[] = [];
  for (let j = SWING_K; j < candles.length - SWING_K; j++) {
    let isSwing = true;
    for (let d = 1; d <= SWING_K && isSwing; d++) {
      if (side === "low") {
        if (candles[j]!.low > candles[j - d]!.low || candles[j]!.low > candles[j + d]!.low) isSwing = false;
      } else {
        if (candles[j]!.high < candles[j - d]!.high || candles[j]!.high < candles[j + d]!.high) isSwing = false;
      }
    }
    if (isSwing) swings.push({ confirmedAt: j + SWING_K, value: side === "low" ? candles[j]!.low : candles[j]!.high });
  }
  return swings;
}

function nearestSwingLevel(candles: OHLCPoint[], side: "support" | "resistance"): (number | null)[] {
  const swings = confirmedSwings(candles, side === "support" ? "low" : "high");
  return candles.map((c, i) => {
    const usable = swings.filter((sw) => sw.confirmedAt <= i).slice(-50);
    let best: number | null = null;
    for (const sw of usable) {
      if (side === "support" && sw.value < c.close && (best == null || sw.value > best)) best = sw.value;
      if (side === "resistance" && sw.value > c.close && (best == null || sw.value < best)) best = sw.value;
    }
    return best;
  });
}

/** The most recent CONFIRMED swing high/low price at each bar (the raw
 * pivot the strategy can reference directly). */
function latestSwingSeries(candles: OHLCPoint[], side: "high" | "low"): (number | null)[] {
  const swings = confirmedSwings(candles, side);
  return candles.map((_, i) => {
    const usable = swings.filter((sw) => sw.confirmedAt <= i);
    return usable.length ? usable[usable.length - 1]!.value : null;
  });
}

/** Market-structure event: fires (1) on the bar a new confirmed swing
 * high forms ABOVE the previous swing high ("higher high"), or a swing
 * low BELOW the previous ("lower low"). 0 on other bars. */
function structureEventSeries(candles: OHLCPoint[], side: "high" | "low"): (number | null)[] {
  const swings = confirmedSwings(candles, side);
  const out = new Array<number | null>(candles.length).fill(0);
  for (let s = 1; s < swings.length; s++) {
    const isEvent = side === "high" ? swings[s]!.value > swings[s - 1]!.value : swings[s]!.value < swings[s - 1]!.value;
    if (isEvent && swings[s]!.confirmedAt < candles.length) out[swings[s]!.confirmedAt] = 1;
  }
  return out;
}

/** Floor-trader daily pivot levels from the last CLOSED day's H/L/C:
 * P=(H+L+C)/3, R1=2P−L, S1=2P−H, R2=P+(H−L), S2=P−(H−L). Same no-peek
 * rule as the other session levels. */
function pivotLevelSeries(candles: OHLCPoint[], kind: "pivotP" | "pivotR1" | "pivotR2" | "pivotS1" | "pivotS2"): (number | null)[] {
  const days = resampleCandles(candles, 1440);
  const sizeMs = 1440 * 60_000;
  return candles.map((c) => {
    const t = c.date.getTime();
    let day: OHLCPoint | null = null;
    for (const b of days) {
      if (b.date.getTime() + sizeMs <= t) day = b;
      else break;
    }
    if (!day) return null;
    const p = (day.high + day.low + day.close) / 3;
    const range = day.high - day.low;
    switch (kind) {
      case "pivotP":
        return p;
      case "pivotR1":
        return 2 * p - day.low;
      case "pivotS1":
        return 2 * p - day.high;
      case "pivotR2":
        return p + range;
      case "pivotS2":
        return p - range;
    }
  });
}

function rollingExtreme(candles: OHLCPoint[], period: number, side: "high" | "low"): (number | null)[] {
  return candles.map((_, i) => {
    if (i < period) return null;
    let extreme = side === "high" ? -Infinity : Infinity;
    for (let j = i - period; j < i; j++) {
      const v = side === "high" ? candles[j]!.high : candles[j]!.low;
      extreme = side === "high" ? Math.max(extreme, v) : Math.min(extreme, v);
    }
    return extreme;
  });
}

// ---------------------------------------------------------------------------
// Multi-timeframe layers

/** Aggregates execution-timeframe candles into fixed higher-timeframe
 * buckets (epoch-aligned). The last bucket is dropped unless the data
 * clearly runs past its end — only CLOSED bars may feed layer rules. */
export function resampleCandles(candles: OHLCPoint[], minutes: number): OHLCPoint[] {
  if (!candles.length || minutes <= 0) return [];
  const sizeMs = minutes * 60_000;
  const out: OHLCPoint[] = [];
  let bucketStart = -1;
  for (const c of candles) {
    const t = c.date.getTime();
    const start = Math.floor(t / sizeMs) * sizeMs;
    if (start !== bucketStart) {
      out.push({ date: new Date(start), open: c.open, high: c.high, low: c.low, close: c.close, volume: c.volume });
      bucketStart = start;
    } else {
      const bar = out[out.length - 1]!;
      bar.high = Math.max(bar.high, c.high);
      bar.low = Math.min(bar.low, c.low);
      bar.close = c.close;
      bar.volume += c.volume;
    }
  }
  return out;
}

interface PreparedLayer {
  sizeMs: number;
  barTimes: number[]; // resampled bucket start times
  rules: Array<{ rule: Rule; left: (number | null)[]; right: (number | null)[] }>;
}

function prepareLayer(layer: StrategyLayer, candles: OHLCPoint[], isIntraday: boolean): PreparedLayer | null {
  const meta = layerTimeframes.find((tf) => tf.id === layer.timeframe);
  if (!meta || !layer.rules.length) return null;
  const resampled = resampleCandles(candles, meta.minutes);
  return {
    sizeMs: meta.minutes * 60_000,
    barTimes: resampled.map((c) => c.date.getTime()),
    rules: layer.rules.map((rule) => ({
      rule,
      left: seriesFor(rule.left.kind, resampled, isIntraday, rule.left.value),
      right: seriesFor(rule.right.kind, resampled, isIntraday, rule.right.value),
    })),
  };
}

/** True when every layer rule holds on the last CLOSED layer bar at
 * execution time `timeMs`. No closed bar yet → the layer blocks. */
function layerHolds(prepared: PreparedLayer, timeMs: number): boolean {
  let index = -1;
  for (let i = 0; i < prepared.barTimes.length; i++) {
    if (prepared.barTimes[i]! + prepared.sizeMs <= timeMs) index = i;
    else break;
  }
  if (index < 0) return false;
  return prepared.rules.every((r) => ruleHolds(r.rule, r.left, r.right, index));
}

function ruleHolds(rule: Rule, left: (number | null)[], right: (number | null)[], i: number): boolean {
  const l = left[i];
  const r = right[i];
  if (l == null || r == null) return false;
  switch (rule.cmp) {
    case "greaterThan":
      return l > r;
    case "lessThan":
      return l < r;
    case "crossesAbove": {
      const lp = left[i - 1];
      const rp = right[i - 1];
      return lp != null && rp != null && lp <= rp && l > r;
    }
    case "crossesBelow": {
      const lp = left[i - 1];
      const rp = right[i - 1];
      return lp != null && rp != null && lp >= rp && l < r;
    }
  }
}

export interface BacktestTrade {
  entryIndex: number;
  exitIndex: number;
  entryPrice: number;
  exitPrice: number;
  returnPct: number;
}

/** Bar-level, cost-aware performance summary — computed the same way for
 * the strategy and its benchmark so the two are directly comparable
 * (the thesis's method: judge a strategy AGAINST buy-and-hold, not in a
 * vacuum). Risk-free rate is taken as 0 (returns are excess over cash). */
export interface PerfStats {
  totalReturnPct: number;
  maxDrawdownPct: number;
  /** Annualized / risk-adjusted metrics are null on windows shorter than
   * ~6 months: annualizing a few weeks of data produces absurd numbers
   * and unstable ratios (a 1-day sample "annualizes" to billions of %).
   * Total return and drawdown stay valid on any window. */
  annualizedReturnPct: number | null;
  sharpe: number | null;
  sortino: number | null;
}

export interface BacktestResult {
  trades: BacktestTrade[];
  winRate: number | null;
  totalReturnPct: number;
  maxDrawdownPct: number;
  avgReturnPct: number | null;
  openAtEnd: boolean;
  /** Honesty flag: below ~30 trades a win rate is an anecdote, not
   * evidence (Kernel: analytics need thresholds). */
  tooFewTrades: boolean;
  /** The strategy vs. a buy-and-hold benchmark on the same window,
   * both mark-to-market per bar and cost-aware. */
  stats: PerfStats;
  benchmark: PerfStats;
  /** Prop-rule check, present when the strategy's goal is "combine" and
   * rules are set. */
  prop?: PropCheck;
}

/** Per-bar mark-to-market returns while the strategy holds a position
 * (0 in cash). Entry uses the fill price, exit the exit price; the
 * round-trip cost is charged on the exit bar. */
function strategyBarReturns(candles: OHLCPoint[], trades: BacktestTrade[], dir: number, costPct?: number): number[] {
  const rets = new Array<number>(candles.length).fill(0);
  const cost = (costPct ?? 0) / 100;
  for (const t of trades) {
    for (let i = t.entryIndex; i <= t.exitIndex && i < candles.length; i++) {
      const prev = i === t.entryIndex ? t.entryPrice : candles[i - 1]!.close;
      const cur = i === t.exitIndex ? t.exitPrice : candles[i]!.close;
      if (prev > 0) rets[i] = dir * (cur / prev - 1);
    }
    if (t.exitIndex < candles.length) rets[t.exitIndex]! -= 2 * cost;
  }
  return rets;
}

/** Buy-and-hold (always long) on closes — the benchmark every strategy
 * is measured against. One entry cost. */
function benchmarkBarReturns(candles: OHLCPoint[], costPct?: number): number[] {
  const rets = new Array<number>(candles.length).fill(0);
  for (let i = 1; i < candles.length; i++) {
    const prev = candles[i - 1]!.close;
    if (prev > 0) rets[i] = candles[i]!.close / prev - 1;
  }
  if (candles.length > 1) rets[1]! -= (costPct ?? 0) / 100;
  return rets;
}

export function perfStats(barReturns: number[], candles: OHLCPoint[]): PerfStats {
  let equity = 1;
  let peak = 1;
  let maxDD = 0;
  for (const r of barReturns) {
    equity *= 1 + r;
    peak = Math.max(peak, equity);
    if (peak > 0) maxDD = Math.max(maxDD, (peak - equity) / peak);
  }
  const total = equity - 1;
  const first = candles[0]?.date.getTime() ?? 0;
  const last = candles[candles.length - 1]?.date.getTime() ?? 0;
  const years = Math.max((last - first) / (365.25 * 86400_000), 1e-6);
  const periodsPerYear = Math.max(barReturns.length / years, 1);
  const n = barReturns.length || 1;
  const mean = barReturns.reduce((a, b) => a + b, 0) / n;
  const variance = barReturns.reduce((a, b) => a + (b - mean) ** 2, 0) / n;
  const sd = Math.sqrt(variance);
  // Target-downside deviation (MAR = 0): squared negatives over all bars.
  const downVar = barReturns.reduce((a, b) => a + (b < 0 ? b * b : 0), 0) / n;
  const downSd = Math.sqrt(downVar);
  const ann = Math.sqrt(periodsPerYear);
  // Need a meaningful window before annualized / risk-adjusted numbers
  // mean anything (Yahoo caps intraday history at ~60 days, so those
  // charts legitimately show only total return + drawdown).
  const longEnough = years >= 0.5 && barReturns.length >= 40;
  return {
    totalReturnPct: total * 100,
    maxDrawdownPct: maxDD * 100,
    annualizedReturnPct: longEnough ? (Math.pow(1 + total, 1 / years) - 1) * 100 : null,
    sharpe: longEnough && sd > 1e-12 ? (mean / sd) * ann : null,
    sortino: longEnough && downSd > 1e-12 ? (mean / downSd) * ann : null,
  };
}

/**
 * Bar-by-bar backtest: a signal on bar i fills at bar i+1's open (no
 * look-ahead); TP/SL check against each bar's high/low; any exit rule
 * closes at that bar's close. Equity compounds per trade.
 */
export function backtest(strategy: Strategy, candles: OHLCPoint[], isIntraday: boolean): BacktestResult {
  const dir = strategy.direction === "long" ? 1 : -1;
  const sides = (rules: Rule[]) =>
    rules.map((rule) => ({
      rule,
      left: seriesFor(rule.left.kind, candles, isIntraday, rule.left.value),
      right: seriesFor(rule.right.kind, candles, isIntraday, rule.right.value),
    }));
  const entrySeries = sides(strategy.entry);
  const exitSeries = sides(strategy.exit);
  // Higher-timeframe filter layers, resampled from the execution candles.
  const layers = [strategy.trend, strategy.setup]
    .map((layer) => (layer ? prepareLayer(layer, candles, isIntraday) : null))
    .filter((l): l is PreparedLayer => l != null);

  const trades: BacktestTrade[] = [];
  let position: { entryIndex: number; entryPrice: number } | null = null;

  const entryFires = (i: number): boolean => {
    if (!entrySeries.length) return false;
    if (strategy.entryMode === "score") {
      let score = 0;
      for (const s of entrySeries) if (ruleHolds(s.rule, s.left, s.right, i)) score += s.rule.weight ?? 1;
      return score >= (strategy.minScore ?? entrySeries.length);
    }
    return entrySeries.every((s) => ruleHolds(s.rule, s.left, s.right, i));
  };

  for (let i = 1; i < candles.length - 1; i++) {
    if (!position) {
      const barTime = candles[i]!.date.getTime();
      const allHold = entryFires(i) && layers.every((layer) => layerHolds(layer, barTime));
      if (allHold) {
        position = { entryIndex: i + 1, entryPrice: candles[i + 1]!.open };
      }
      continue;
    }

    const bar = candles[i]!;
    if (i >= position.entryIndex) {
      let exitPrice: number | null = null;
      const tp = strategy.takeProfitPct;
      const sl = strategy.stopLossPct;
      if (tp != null && tp > 0) {
        const target = position.entryPrice * (1 + (dir * tp) / 100);
        if ((dir > 0 && bar.high >= target) || (dir < 0 && bar.low <= target)) exitPrice = target;
      }
      if (exitPrice == null && sl != null && sl > 0) {
        const stop = position.entryPrice * (1 - (dir * sl) / 100);
        if ((dir > 0 && bar.low <= stop) || (dir < 0 && bar.high >= stop)) exitPrice = stop;
      }
      if (exitPrice == null && exitSeries.some((s) => ruleHolds(s.rule, s.left, s.right, i))) {
        exitPrice = bar.close;
      }
      if (exitPrice != null) {
        const returnPct = dir * ((exitPrice - position.entryPrice) / position.entryPrice) * 100;
        trades.push({ entryIndex: position.entryIndex, exitIndex: i, entryPrice: position.entryPrice, exitPrice, returnPct });
        position = null;
      }
    }
  }

  // Equity curve for total return and max drawdown.
  let equity = 1;
  let peak = 1;
  let maxDD = 0;
  for (const trade of trades) {
    equity *= 1 + trade.returnPct / 100;
    peak = Math.max(peak, equity);
    maxDD = Math.max(maxDD, (peak - equity) / peak);
  }

  const wins = trades.filter((t) => t.returnPct > 0).length;
  const barRets = strategyBarReturns(candles, trades, dir, strategy.costPct);
  return {
    trades,
    winRate: trades.length ? (wins / trades.length) * 100 : null,
    totalReturnPct: (equity - 1) * 100,
    maxDrawdownPct: maxDD * 100,
    avgReturnPct: trades.length ? trades.reduce((s, t) => s + t.returnPct, 0) / trades.length : null,
    openAtEnd: position != null,
    tooFewTrades: trades.length > 0 && trades.length < 30,
    stats: perfStats(barRets, candles),
    benchmark: perfStats(benchmarkBarReturns(candles, strategy.costPct), candles),
    prop:
      strategy.goal === "combine" && strategy.propRules && strategy.propRules.accountSize > 0
        ? propCheck(barRets, candles, strategy.propRules)
        : undefined,
  };
}

// ---------------------------------------------------------------------------

const STRATEGIES_KEY = "strategies";

export class StrategyStore {
  strategies: Strategy[] = [];
  readonly changed = new Emitter();

  async init(): Promise<void> {
    this.strategies = await storageGet<Strategy[]>(STRATEGIES_KEY, []);
    onStorageChange<Strategy[]>(STRATEGIES_KEY, (v) => {
      this.strategies = v ?? [];
      this.changed.emit();
    });
    this.changed.emit();
  }

  async save(strategy: Strategy): Promise<void> {
    const idx = this.strategies.findIndex((s) => s.id === strategy.id);
    if (idx >= 0) this.strategies[idx] = strategy;
    else this.strategies.push(strategy);
    await storageSet(STRATEGIES_KEY, this.strategies);
    this.changed.emit();
  }

  async remove(id: string): Promise<void> {
    this.strategies = this.strategies.filter((s) => s.id !== id);
    await storageSet(STRATEGIES_KEY, this.strategies);
    this.changed.emit();
  }
}

export const strategyStore = new StrategyStore();

// ---------------------------------------------------------------------------
// Live signal alerts: a saved strategy can be subscribed to a (symbol,
// interval); the background worker evaluates the entry rules on fresh
// candles and raises a notification when they trigger — TickAhead watches
// the setup so the trader doesn't have to.

export interface SignalSubscription {
  strategyId: string;
  symbol: string;
  interval: string;
}

const SIGNAL_SUBS_KEY = "strategySignalSubs";

export async function getSignalSubscriptions(): Promise<SignalSubscription[]> {
  return storageGet<SignalSubscription[]>(SIGNAL_SUBS_KEY, []);
}

export async function toggleSignalSubscription(sub: SignalSubscription): Promise<boolean> {
  const subs = await getSignalSubscriptions();
  const idx = subs.findIndex((s) => s.strategyId === sub.strategyId && s.symbol === sub.symbol && s.interval === sub.interval);
  if (idx >= 0) {
    subs.splice(idx, 1);
    await storageSet(SIGNAL_SUBS_KEY, subs);
    return false;
  }
  subs.push(sub);
  await storageSet(SIGNAL_SUBS_KEY, subs);
  return true;
}

export async function hasSignalSubscription(sub: SignalSubscription): Promise<boolean> {
  const subs = await getSignalSubscriptions();
  return subs.some((s) => s.strategyId === sub.strategyId && s.symbol === sub.symbol && s.interval === sub.interval);
}

/** Whether the strategy's entry conditions hold on the most recent bar.
 * Returns the bar's timestamp so callers can dedup one alert per bar. */
export function latestSignal(strategy: Strategy, candles: OHLCPoint[], isIntraday: boolean): { barTime: number } | null {
  if (candles.length < 3 || !strategy.entry.length) return null;
  const i = candles.length - 1;
  // Layer gating applies to live signals exactly as it does to backtests
  // (same closed-bar semantics — one engine, no divergence).
  const barTime = candles[i]!.date.getTime();
  for (const layer of [strategy.trend, strategy.setup]) {
    if (!layer) continue;
    const prepared = prepareLayer(layer, candles, isIntraday);
    if (prepared && !layerHolds(prepared, barTime)) return null;
  }
  const ruleOk = (rule: Rule): boolean => {
    const left = seriesFor(rule.left.kind, candles, isIntraday, rule.left.value);
    const right = seriesFor(rule.right.kind, candles, isIntraday, rule.right.value);
    const l = left[i];
    const r = right[i];
    if (l == null || r == null) return false;
    switch (rule.cmp) {
      case "greaterThan":
        return l > r;
      case "lessThan":
        return l < r;
      case "crossesAbove": {
        const lp = left[i - 1];
        const rp = right[i - 1];
        return lp != null && rp != null && lp <= rp && l > r;
      }
      case "crossesBelow": {
        const lp = left[i - 1];
        const rp = right[i - 1];
        return lp != null && rp != null && lp >= rp && l < r;
      }
    }
  };
  // Same all-vs-score semantics as the backtest — one engine.
  let holds: boolean;
  if (strategy.entryMode === "score") {
    let score = 0;
    for (const rule of strategy.entry) if (ruleOk(rule)) score += rule.weight ?? 1;
    holds = score >= (strategy.minScore ?? strategy.entry.length);
  } else {
    holds = strategy.entry.every(ruleOk);
  }
  return holds ? { barTime: candles[i]!.date.getTime() } : null;
}
